import React, { useState, useContext, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkSquareAlt, faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import PanelSearchModal from '../../../components/modal/PanelSearchModal';
import ConceptCardHover from '../../../components/common/ConceptCardHover';
import KeyContext from '../../../CMFW/context/KeyContext';
import ModuleContext from '../../../CMFW/context/ModuleContext';
import { useCurrentItemValue } from '../../../hooks/useCurrentItemValue';
import { IInflectEnding } from '../../../models/ConjugationRuleEndingsModel';
import ConjugationRuleEndingsModel from '../../../models/ConjugationRuleEndingsModel';
import TextInput from '../../../CMFW/input/TextInput';
import { IRefIds } from '../../../constants/refs';

interface IProps {
  courseId: number;
}

const ConjugationRuleCell: React.FC<IProps> = ({ courseId }) => {
  const module = useContext(ModuleContext) as ConjugationRuleEndingsModel;
  const aggregatedKeyName = useContext(KeyContext);

  const moduleItemValue: IInflectEnding = module.getItemValue.bind(module)(aggregatedKeyName);
  const moduleItemValueUpdated: IInflectEnding = module.getItemUpdatedValue.bind(module)(aggregatedKeyName);

  // Use moduleItemValue as initial value if available, otherwise null
  const [currentValue, setCurrentValue] = useCurrentItemValue<IInflectEnding | null>(moduleItemValue || null, null);
  const [panelModalOpen, setPanelModalOpen] = useState(false);
  const [isActive, setIsActive] = useState(!!currentValue?.ending);

  useEffect(() => {
    if (moduleItemValueUpdated) {
      setCurrentValue(moduleItemValueUpdated);
      setIsActive(!!moduleItemValueUpdated?.ending);
    }
  }, [moduleItemValueUpdated, setCurrentValue]);

  useEffect(() => {
    setIsActive(!!currentValue?.ending);
  }, [currentValue]);

  const handlePanelSelect = (panel: any) => {
    const panelKeyPath = aggregatedKeyName + '.panelId';
    module.updateItemValue.bind(module)(panelKeyPath, panel.id);
    setCurrentValue((prev) => {
      if (prev) {
        return { ...prev, panelId: panel.id };
      }
      return { index: 0, inflectId: 0, ending: '', group: 1, panelId: panel.id } as IInflectEnding;
    });
    setPanelModalOpen(false);
  };

  const handlePanelRemove = () => {
    if (window.confirm('Do you want to remove the panel?')) {
      const panelKeyPath = aggregatedKeyName + '.panelId';
      module.updateItemValue.bind(module)(panelKeyPath, null);
      setCurrentValue((prev) => {
        if (prev) {
          return { ...prev, panelId: null };
        }
        return null;
      });
    }
  };

  const handlePanelClick = () => {
    if (currentValue?.panelId) {
      handlePanelRemove();
    } else {
      setPanelModalOpen(true);
    }
  };

  // Determine if this cell is in the first column
  const isFirstColumn = useMemo(() => {
    const inflectId = currentValue?.inflectId;
    if (!inflectId) return false;

    try {
      const cells = module.getTableCells();
      const currentCell = cells.find((c) => c.id === inflectId);
      if (!currentCell) return false;

      // Find all cells in the same row and sort by column
      const rowCells = cells.filter((c) => c.row === currentCell.row && c.table === currentCell.table);
      rowCells.sort((a, b) => a.column - b.column);

      // Check if this is the first column in the row
      return rowCells[0]?.id === inflectId;
    } catch (error) {
      return false;
    }
  }, [currentValue?.inflectId, module]);

  const handleActivateCell = () => {
    setIsActive(true);
  };

  // Render the component even if currentValue is null
  return (
    <>
      {!isActive && !currentValue?.ending ? (
        <InactiveCellContainer>
          {isFirstColumn && (
            <GroupBadge title={`Group ${currentValue?.group || 1}`}>{currentValue?.group || 1}</GroupBadge>
          )}
          <ActivateButtonWrapper>
            <ActivateButton onClick={handleActivateCell} title="Click to edit">
              <FontAwesomeIcon icon={faPencilAlt} />
            </ActivateButton>
          </ActivateButtonWrapper>
        </InactiveCellContainer>
      ) : (
        <GroupContainer>
          {isFirstColumn && (
            <GroupBadge title={`Group ${currentValue?.group || 1}`}>{currentValue?.group || 1}</GroupBadge>
          )}
          <TextInput keyName={'ending'} />
          {!!currentValue?.panelId ? (
            <ConceptCardHover
              conceptRef={IRefIds.panels}
              refId={currentValue?.panelId || 0}
              courseId={courseId}
              showLabel={false}
            >
              <IconButton hasPanel={!!currentValue?.panelId} onClick={handlePanelClick}>
                <FontAwesomeIcon icon={faExternalLinkSquareAlt} />
              </IconButton>
            </ConceptCardHover>
          ) : (
            <IconButton hasPanel={!!currentValue?.panelId} onClick={handlePanelClick}>
              <FontAwesomeIcon icon={faExternalLinkSquareAlt} />
            </IconButton>
          )}
        </GroupContainer>
      )}

      {panelModalOpen && (
        <PanelSearchModal courseId={courseId} onSelect={handlePanelSelect} onClose={() => setPanelModalOpen(false)} />
      )}
    </>
  );
};

const IconButton = styled.button<{ hasPanel: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1px;
  border: none;
  background: transparent;
  color: ${(props) => (props.hasPanel ? '#4CAF50' : '#ababab')};
  border-radius: 2px;
  cursor: pointer;
  height: 16px;
  width: 16px;
  margin-right: -6px;
  margin-left: 1px;

  &:hover {
    background: ${(props) => (props.hasPanel ? '#E8F5E9' : '#f5f5f5')};
  }
`;

const GroupContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 4px 0;
  position: relative;

  > :nth-child(2) {
    flex: 1;
  }
`;

const GroupBadge = styled.div`
  position: absolute;
  left: -14px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  background: #9e9e9e;
  border: none;
  border-radius: 2px;
  font-size: 11px;
  font-weight: 600;
  color: white;
  flex-shrink: 0;
  padding-top: 2px;
`;

const InactiveCellContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 4px 0;
  position: relative;
  height: 36px;
  width: 100%;
`;

const ActivateButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 28px;
`;

const ActivateButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: none;
  background: transparent;
  color: #bdbdbd;
  border-radius: 2px;
  cursor: pointer;
  font-size: 12px;
  height: 28px;
  width: 28px;
  transition: all 0.2s ease;

  &:hover {
    background: #f5f5f5;
    color: #757575;
  }
`;

export default ConjugationRuleCell;
